import React, { useContext, useState, useEffect } from "react";
import {
  useTheme,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCookies } from "react-cookie";
import { UserContext } from "../../context/UserContext";
import MonthNavigator from "./MonthNavigator";
import { useNavigate } from "react-router-dom";
import BillingService from "../../services/BillingService";

interface BillingPatient {
  patient_id: string;
  last_name: string;
  first_name: string;
  cpt: Record<
    string,
    {
      text: string;
      qty: number;
      date?: string;
      transmission_days?: any;
    }
  >;
  measurement_days: number;
  rpm_call_time: number;
  call_log?: any[];
}

interface CptSummaryRow {
  cptCode: string;
  description: string;
  totalUnits: number;
  ratePerUnit: number;
  totalRevenue: number;
}



/*
    This page handles the billing activities for the CCM module.
    The following activities are available:
     - auto-retrieve billing summary for a month (in-progress and historical)
     - retrieve audit information for a month
*/
export const BillingManagement: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cookies] = useCookies(["access_token"]);
  const context = useContext(UserContext);
  const [user] = useState(context?.user);
  const my_organization_id = user?.organization || "";
  const token = cookies.access_token;

  // State for the billing data
  const [patientBillingDetails, setPatientBillingDetails] = useState<BillingPatient[]>([]);
  const [summaryByCpt, setSummaryByCpt] = useState<CptSummaryRow[]>([]);
  const [tableHeight, setTableHeight] = useState("500px"); // Default height
  const [filterText, setFilterText] = useState<string>("");
  const [showPatientsWithNoQualifyingCodes, setShowPatientsWithNoQualifyingCodes] = useState<boolean>(false);
  const [billingDataLoaded, setBillingDataLoaded] = useState<boolean>(false);
  const [showPatientName, ] = useState<boolean>(false);
  const today = new Date();
  const currentMonthString = `${today.getFullYear()}-${("0" + (today.getMonth() + 1)).slice(-2)}`;
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonthString);

  // When the month changes, clear the table data and reset the loaded flag.
  const handleMonthChange = (newMonth: string) => {
    if (newMonth === selectedMonth) return;
    setSelectedMonth(newMonth);
    setPatientBillingDetails([]);
    setSummaryByCpt([]);
    setBillingDataLoaded(false);
  };

  // Compute a formatted month label for display in the header.
  const monthDate = new Date(selectedMonth + "-01");
  const monthLabel = monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  useEffect(() => {
    // Adjust table height dynamically based on window size.
    const updateHeight = () => {
      const maxHeight = window.innerHeight - 250; // Adjust based on layout.
      setTableHeight(`${maxHeight}px`);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // Auto-retrieve billing data when selectedMonth changes.
  useEffect(() => {
    let isMounted = true;
    const fetchBillingData = async () => {
      if (isMounted) {
        setBillingDataLoaded(false);
        setPatientBillingDetails([]);
        setSummaryByCpt([]);
      }
      try {
        const response = await BillingService.billingSummary(token, my_organization_id, selectedMonth).catch(
          (error) => {
            console.error("Error in handleCreateBilling", error);
          }
        );
        if (isMounted) {
          setPatientBillingDetails(response);
          console.log("response:", response);
        }
      } catch (error) {
        console.error("Error in fetching billing data", error);
      } finally {
        if (isMounted) {
          setBillingDataLoaded(true);
        }
      }
    };

    fetchBillingData();

    return () => {
      isMounted = false;
    };
  }, [selectedMonth, token, my_organization_id]);

  const lowerCaseFilter = filterText.toLowerCase();

  return (
    <Box sx={{ flexGrow: 1, padding: theme.spacing(2) }}>
      <Grid container spacing={4}>
        {/* Header Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: theme.spacing(2), overflow: "hidden" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={4}>
                <h2>{t("Billing Management")}</h2>
              </Grid>
              <Grid item xs={4}>
                <MonthNavigator onMonthChange={handleMonthChange} value={selectedMonth} />
              </Grid>
              <Grid item xs={4} style={{ textAlign: "right" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/billing/recipients")}
                >
                  {t("Manage Billing Recipients")}
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Summary by CPT Code Section */}
        {summaryByCpt.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: theme.spacing(2), overflow: "auto" }}>
              <h3>{t("Summary by CPT Code")}</h3>
              <TableContainer sx={{ maxHeight: tableHeight }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("CPT Code")}</TableCell>
                      <TableCell>{t("Description")}</TableCell>
                      <TableCell>{t("Total Units")}</TableCell>
                      <TableCell>{t("Rate per Unit ($)")}</TableCell>
                      <TableCell>{t("Total Revenue ($)")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summaryByCpt.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.cptCode}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{row.totalUnits}</TableCell>
                        <TableCell>{row.ratePerUnit}</TableCell>
                        <TableCell>{row.totalRevenue}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* Patient Billing Details Section */}
        {patientBillingDetails.length > 0 ? (
          <Grid item xs={12}>
            <Paper sx={{ p: theme.spacing(2), overflow: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: theme.spacing(2),
                }}
              >
                <h3>{`Patient Billing Details - ${monthLabel}`}</h3>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showPatientsWithNoQualifyingCodes}
                        onChange={(e) => setShowPatientsWithNoQualifyingCodes(e.target.checked)}
                      />
                    }
                    label={t("Show patients with no qualifying codes")}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    sx={{ ml: 2 }}
                  />
                </Box>
              </Box>
              <TableContainer sx={{ maxHeight: tableHeight }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Patient ID")}</TableCell>
                      { showPatientName && <TableCell>{t("Patient Name")}</TableCell>}
                      <TableCell>{t("CPT Code")}</TableCell>
                      <TableCell>{t("Units")}</TableCell>
                      <TableCell>{t("Service Description")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {patientBillingDetails.map((patient) => {
                      console.log ("patient:", patient);
                      const patientName = patient.last_name +", " + patient.first_name || patient.patient_id;
                      const patientIdMatch = patient.patient_id.toLowerCase().includes(lowerCaseFilter);
                      const patientNameMatch = patientName.toLowerCase().includes(lowerCaseFilter);
                      const cptEntries = Object.entries(patient.cpt);

                      // When there are no CPT codes...
                      if (cptEntries.length === 0) {
                        if ((filterText === "" || patientIdMatch || patientNameMatch) && showPatientsWithNoQualifyingCodes) {
                          return (
                            <TableRow key={patient.patient_id}>
                              <TableCell sx={{ verticalAlign: "top" }}>{patient.patient_id}</TableCell>
                              { showPatientName && <TableCell sx={{ verticalAlign: "top" }}>{patientName}</TableCell>}
                              <TableCell colSpan={3}>No CPT codes</TableCell>
                            </TableRow>
                          );
                        }
                        return null;
                      }

                      // When there are CPT codes, filter them if patient info doesn't match.
                      let finalCptEntries: Array<
                        [string, { text: string; qty: number; date?: string; transmission_days?: any }]
                      > = [];
                      if (patientIdMatch || patientNameMatch) {
                        finalCptEntries = cptEntries;
                      } else {
                        finalCptEntries = cptEntries.filter(([cptCode, details]) => {
                          return (
                            cptCode.toLowerCase().includes(lowerCaseFilter) ||
                            details.text.toLowerCase().includes(lowerCaseFilter)
                          );
                        });
                      }

                      if (finalCptEntries.length === 0) {
                        return null;
                      }

                      return (
                        <React.Fragment key={patient.patient_id}>
                          {finalCptEntries.map(([cptCode, details], index) => (
                            <TableRow key={`${patient.patient_id}-${cptCode}`}>
                              {index === 0 && (
                                <>
                                  <TableCell rowSpan={finalCptEntries.length} sx={{ verticalAlign: "top" }}>
                                    {patient.patient_id}
                                  </TableCell>
                                  { showPatientName && <TableCell rowSpan={finalCptEntries.length} sx={{ verticalAlign: "top" }}>
                                    {patientName} 
                                  </TableCell> }
                                </>
                              )}
                              <TableCell>{cptCode}</TableCell>
                              <TableCell align="center">{details.qty}</TableCell>
                              <TableCell>{details.text}</TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        ) : (
          // If billingResponse is empty, show a message
          <Grid item xs={12}>
            <Paper sx={{ p: theme.spacing(2) }}>
              <h3>{`Patient Billing Details - ${monthLabel}`}</h3>
              <Box sx={{ textAlign: "center", padding: theme.spacing(2) }}>
                {!billingDataLoaded ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1 }} /> {t("Retrieving billing data...")}
                  </>
                ) : (
                  t("No data found for this month")
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default BillingManagement;